import { useRef, useState, useEffect, useCallback } from 'react';

interface PullToRefreshState {
  containerRef: React.RefObject<HTMLDivElement>;
  isPulling: boolean;
  isRefreshing: boolean;
  pullDistance: number;
  pullThreshold: number;
}

export function usePullToRefresh(onRefresh: () => void): PullToRefreshState {
  const containerRef = useRef<HTMLDivElement>(null!);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const pullThreshold = 80;

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling) return;
    const delta = Math.max(0, e.touches[0].clientY - startY.current);
    setPullDistance(delta * 0.5);
  }, [isPulling]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= pullThreshold) {
      setIsRefreshing(true);
      setIsPulling(false);
      setPullDistance(0);
      Promise.resolve(onRefresh()).finally(() => {
        setIsRefreshing(false);
      });
    } else {
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [pullDistance, pullThreshold, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { containerRef, isPulling, isRefreshing, pullDistance, pullThreshold };
}
